import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

type AxiosLike = {
  response?: { data?: Record<string, string>; status?: number };
  message?: string;
};

// TypeScript Interfaces (from API documentation)
export interface HotSignals {
  tour: boolean; // Tour/showing requested
  urgency: boolean; // Urgent timeline mentioned
  buyingIntent: boolean; // Strong buying signals
}

// Active Cadence interface for outreach automation
export interface ActiveCadence {
  strategyId: string; // The outreach strategy being used (e.g., "nurture", "convert", "re-engage")
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  currentStep: number; // Which step of the cadence sequence the contact is on
  totalAttempts: number; // Total number of cadence attempts executed so far
  nextScheduledAt: string | null; // ISO 8601 datetime - when the next cadence step is scheduled
  expiresAt: string | null; // ISO 8601 datetime - when this cadence will automatically expire/end
  startedAt: string | null; // When the cadence was started
  lastExecutedAt: string | null; // When the last cadence step was executed
  lastDecision: string | null; // The last decision made (e.g., "continue", "pause")
}

export interface FollowUpDecision {
  id: string;
  contactId: string;
  organizationId: string;
  scheduledFor: string; // ISO 8601 datetime
  channel: 'call' | 'email' | 'sms' | 'text';
  reasoning: string; // Rich reasoning for sales team
  priority: 'high' | 'medium' | 'low';
  timingType: 'explicit' | 'fuzzy' | 'strategy' | 'default';
  hotSignals: HotSignals;
  source: string;
  executionId?: string;
  createdAt: string;
  activeCadence?: ActiveCadence; // Optional active cadence information
}

export interface NextFollowUpResponse {
  success: boolean;
  data: FollowUpDecision | null;
  cadence?: ActiveCadence;
  message?: string;
}

export interface FollowUpHistoryResponse {
  success: boolean;
  data: FollowUpDecision[];
  count: number;
  hasMore: boolean;
}

export interface FollowUpHistoryOptions {
  organizationId?: string;
  includePast?: boolean;
  limit?: number;
  offset?: number;
}

// Follow-up Stats Interfaces (from API documentation)
// Endpoint: GET /org/:organization_id/followup-stats
export interface FollowUpStatsSummary {
  totalFollowUps: number;
  uniqueContacts: number;
  pendingFollowUps: number;
  completedFollowUps: number;
  periodDays: number;
  outboundSms?: number;
  smsUniqueContacts?: number;
  followUpTimeSavedSeconds?: number;
  smsTimeSavedSeconds?: number;
  totalTimeSavedSeconds?: number;
}

export type FollowUpStatsByChannel = Record<string, number>;

export type FollowUpStrategySegment =
  | 'convert'
  | 'revive'
  | 'engage'
  | 'call_back'
  | 'flywheel'
  | 'other'
  | 'unknown';

export interface FollowUpStatsStrategyBucket {
  followUps: number;
  uniqueContacts: number;
}

export type FollowUpStatsByStrategy = Record<FollowUpStrategySegment, FollowUpStatsStrategyBucket>;

export interface FollowUpTypeBreakdown {
  decisionType: string;
  label: string;
  count: number;
}

export interface FollowUpStatsByTimingType {
  explicit: number;
  strategy: number;
  fuzzy: number;
  default: number;
}

export interface FollowUpStatsByPriority {
  high: number;
  medium: number;
  low: number;
}

export interface FollowUpStatsDailyBreakdown {
  date: string;
  count: number;
}

export interface FollowUpStatsData {
  summary: FollowUpStatsSummary;
  byChannel: FollowUpStatsByChannel;
  byDecisionType?: FollowUpStatsByChannel;
  byTimingType: FollowUpStatsByTimingType;
  byPriority: FollowUpStatsByPriority;
  byStrategy?: FollowUpStatsByStrategy;
  dailyBreakdown: FollowUpStatsDailyBreakdown[];
}

export interface FollowUpStatsResponse {
  success: boolean;
  data: FollowUpStatsData;
  organizationId: string;
  periodStart: string;
  periodEnd: string;
  filterStartDate?: string;
  filterEndDate?: string;
  timestamp: string;
  warning?: string;
}

export interface FollowUpStatsOptions {
  startDate?: string; // ISO format e.g., 2025-12-01
  endDate?: string; // ISO format e.g., 2025-12-31
  days?: number; // Number of days to look back (1-365)
  includeFuture?: boolean; // Include future scheduled follow-ups
}

const FOLLOW_UP_TYPE_LABELS: Record<string, string> = {
  schedule_call: 'Calls',
  schedule_voice: 'Calls',
  voice: 'Calls',
  call: 'Calls',
  send_email: 'Emails',
  schedule_email: 'Emails',
  email: 'Emails',
  send_sms: 'Texts',
  schedule_sms: 'Texts',
  send_text: 'Texts',
  schedule_text: 'Texts',
  sms: 'Texts',
  text: 'Texts',
};

export const EMPTY_FOLLOW_UP_STRATEGY: FollowUpStatsByStrategy = {
  convert: { followUps: 0, uniqueContacts: 0 },
  revive: { followUps: 0, uniqueContacts: 0 },
  engage: { followUps: 0, uniqueContacts: 0 },
  call_back: { followUps: 0, uniqueContacts: 0 },
  flywheel: { followUps: 0, uniqueContacts: 0 },
  other: { followUps: 0, uniqueContacts: 0 },
  unknown: { followUps: 0, uniqueContacts: 0 },
};

export function getFollowUpTypeLabel(decisionType: string): string {
  const normalized = decisionType.trim().toLowerCase();
  return (
    FOLLOW_UP_TYPE_LABELS[normalized] ||
    normalized
      .replace(/^schedule_/, '')
      .replace(/^send_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

export function getFollowUpTypeBreakdown(
  byChannel?: FollowUpStatsByChannel
): FollowUpTypeBreakdown[] {
  return Object.entries(byChannel ?? {})
    .map(([decisionType, count]) => ({
      decisionType,
      label: getFollowUpTypeLabel(decisionType),
      count: Number(count || 0),
    }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function sumFollowUpTypes(
  byChannel: FollowUpStatsByChannel | undefined,
  decisionTypes: string[]
): number {
  return decisionTypes.reduce((sum, key) => sum + Number(byChannel?.[key] || 0), 0);
}

/** Transform raw snake_case API response to camelCase FollowUpDecision */
function transformDecision(raw: Record<string, any>): FollowUpDecision {
  const rawDecision = raw.raw_decision || {};
  const channel = rawDecision.channel || raw.decision_type?.replace('schedule_', '') || 'call';

  return {
    id: raw.decision_id || raw.id || '',
    contactId: raw.contact_id || raw.contactId || '',
    organizationId: raw.organization_id || raw.organizationId || '',
    scheduledFor: raw.scheduled_for || raw.scheduledFor || '',
    channel: channel as FollowUpDecision['channel'],
    reasoning: raw.reasoning || rawDecision.reasoning || '',
    priority: raw.priority || rawDecision.priority || 'medium',
    timingType: raw.timing_type || rawDecision.strategyType || 'default',
    hotSignals: raw.hot_signals ||
      raw.hotSignals || { tour: false, urgency: false, buyingIntent: false },
    source: raw.source || rawDecision.selectionMethod || '',
    executionId: raw.flow_execution_id || raw.executionId,
    createdAt: raw.created_at || raw.createdAt || '',
    activeCadence:
      raw.activeCadence ||
      (raw.strategy_id
        ? {
            strategyId: raw.strategy_id,
            status: 'active' as const,
            currentStep: raw.cadence_step ?? 1,
            totalAttempts: 0,
            nextScheduledAt: raw.scheduled_for || raw.scheduledFor || null,
            expiresAt: null,
            startedAt: null,
            lastExecutedAt: raw.executed_at || null,
            lastDecision: null,
          }
        : undefined),
  };
}

/**
 * Get the next scheduled follow-up for a specific contact
 * @param contactId - The ID of the contact
 * @param organizationId - Optional organization ID filter
 * @returns The next follow-up decision or null if none scheduled
 */
export const getNextFollowUp = async (
  contactId: string,
  organizationId?: string
): Promise<NextFollowUpResponse> => {
  try {
    const response = await remiApi.get<any>(`/api/v1/followups/${contactId}/next`, {
      params: organizationId ? { organization_id: organizationId } : {},
    });

    const result = response.data;
    return {
      ...result,
      data: result.data ? transformDecision(result.data) : null,
    };
  } catch (error) {
    if ((error as AxiosLike).response) {
      // Return the error response from the API
      return {
        success: false,
        data: null,
        message: (error as AxiosLike).response?.data?.error || 'Failed to fetch next follow-up',
      };
    }
    console.error('Error fetching next follow-up:', error);
    throw error;
  }
};

/**
 * Get follow-up history for a contact with pagination support
 * @param contactId - The ID of the contact
 * @param options - Optional query parameters for filtering and pagination
 * @returns Follow-up history with pagination info
 */
export const getFollowUpHistory = async (
  contactId: string,
  options?: FollowUpHistoryOptions
): Promise<FollowUpHistoryResponse> => {
  try {
    const params: Record<string, string> = {};
    if (options?.organizationId) params.organization_id = options.organizationId;
    if (options?.includePast !== undefined) params.include_past = String(options.includePast);
    if (options?.limit !== undefined) params.limit = String(options.limit);
    if (options?.offset !== undefined) params.offset = String(options.offset);

    const response = await remiApi.get<any>(`/api/v1/followups/${contactId}/history`, { params });

    const result = response.data;
    return {
      ...result,
      data: Array.isArray(result.data) ? result.data.map(transformDecision) : [],
    };
  } catch (error) {
    if ((error as AxiosLike).response) {
      // Return empty result on error
      return {
        success: false,
        data: [],
        count: 0,
        hasMore: false,
      };
    }
    console.error('Error fetching follow-up history:', error);
    throw error;
  }
};

/**
 * Get follow-up statistics for an organization
 * Useful for dashboard analytics to show how many follow-ups Remi has scheduled
 * Endpoint: GET /org/:organization_id/followup-stats
 * @param organizationId - The organization ID
 * @param options - Optional query parameters (startDate, endDate, days, includeFuture)
 * @returns Follow-up statistics including channel breakdown
 */
export const getFollowUpStats = async (
  organizationId: string,
  options?: FollowUpStatsOptions
): Promise<FollowUpStatsResponse> => {
  try {
    const params: Record<string, string> = {};
    if (options?.startDate) params.start_date = options.startDate;
    if (options?.endDate) params.end_date = options.endDate;
    if (options?.days !== undefined) params.days = String(options.days);
    if (options?.includeFuture !== undefined) params.include_future = String(options.includeFuture);

    const response = await remiApi.get<FollowUpStatsResponse>(
      `/api/v1/organizations/${organizationId}/followup-stats`,
      { params }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching follow-up stats:', error);
    // Return empty stats on error
    return {
      success: false,
      data: {
        summary: {
          totalFollowUps: 0,
          uniqueContacts: 0,
          pendingFollowUps: 0,
          completedFollowUps: 0,
          periodDays: options?.days || 30,
          outboundSms: 0,
          smsUniqueContacts: 0,
          followUpTimeSavedSeconds: 0,
          smsTimeSavedSeconds: 0,
          totalTimeSavedSeconds: 0,
        },
        byChannel: {
          voice: 0,
          sms: 0,
          email: 0,
        },
        byDecisionType: {},
        byTimingType: {
          explicit: 0,
          strategy: 0,
          fuzzy: 0,
          default: 0,
        },
        byPriority: {
          high: 0,
          medium: 0,
          low: 0,
        },
        byStrategy: EMPTY_FOLLOW_UP_STRATEGY,
        dailyBreakdown: [],
      },
      organizationId,
      periodStart: '',
      periodEnd: '',
      filterStartDate: options?.startDate,
      filterEndDate: options?.endDate,
      timestamp: new Date().toISOString(),
    };
  }
};
